// Requirements a pipeline design is checked against — the generalization of
// the agentOS gate rule. agentosGraph.js is the FLOOR every graph must pass;
// requirements are what THIS service must additionally guarantee (approvals,
// data integrity, performance, completeness), declared on the pipeline
// (`pipeline.requirements`), on the org per environment
// (`org.pipelinePolicy[env].requirements`), or implied by the environment.
//
// Every checker is a path check over the DAG, judged at design time (editor,
// composer) and at run prep; `performance.budget` also reads run evidence.
// Copied to frontend/src/data/requirements.js by scripts/generate-entities.mjs
// — no Node imports.
//
//   requirementProblems(graph, requirements, policy, evidence)
//     → [{ id, kind, text, source, status: 'met'|'unmet'|'needs-run',
//          detail, blockIds, blocking }]
//   requirementRefusals(results, policy) → string[]   (what refuses a run)
import { isModelBlock, sideEffectKind } from './agentosGraph.js';

export const REQUIREMENT_KINDS = {
  approval: {
    label: 'Human approval before a write',
    help: 'Every path from a model (or the named blocks) to a side effect crosses a Human review.',
    params: [
      { key: 'targets', label: 'Side effects', type: 'string', help: 'comma-separated kinds: webhook, s3, dataset, knowledge, container — blank = any' },
      { key: 'fromBlocks', label: 'From blocks (optional)', type: 'string', help: 'comma-separated block ids; blank = every model block' },
    ],
  },
  'integrity.validate-before-write': {
    label: 'Validate before every write',
    help: 'Every path from a read-in block to a side effect crosses a Validate or Pre-flight check.',
    params: [],
  },
  'integrity.sensitive-stays-local': {
    label: 'Sensitive data never reaches a model or leaves',
    help: 'Blocks classified sensitive cannot reach a model block or a webhook unless a Compute block with mask() sits between.',
    params: [
      { key: 'allowWebhook', label: 'Allow webhooks', type: 'select', options: ['false', 'true'], help: 'true = only models are off-limits' },
    ],
  },
  'performance.budget': {
    label: 'Performance budget',
    help: 'The longest path must fit the wall-clock target by static estimate; the run budget is narrowed to match and the last run is the evidence.',
    params: [
      { key: 'wallMs', label: 'End-to-end (ms)', type: 'string', help: 'e.g. 30000 — human review wait is excluded from the estimate' },
      { key: 'modelCalls', label: 'Model calls (max)', type: 'string', help: 'upper bound over the whole graph' },
    ],
  },
  'complete.no-placeholders': {
    label: 'No placeholders',
    help: 'Every step is built: the graph has no Placeholder blocks. Added automatically for UAT and prod.',
    params: [],
  },
};

const KIND_ORDER = Object.keys(REQUIREMENT_KINDS);

// ---- the effective list --------------------------------------------------------
// Pipeline requirements + org requirements for the env (read-only rows,
// source 'org') + the environment's own (source 'env').
export function effectiveRequirements(requirements, policy) {
  const own = (Array.isArray(requirements) ? requirements : []).filter((r) => r && REQUIREMENT_KINDS[r.kind]).map((r) => ({ ...r, source: r.source === 'org' ? 'pipeline' : (r.source || 'pipeline') }));
  const env = policy?.env || 'sandbox';
  const orgList = Array.isArray(policy?.requirements) ? policy.requirements : [];
  const org = orgList.filter((r) => r && REQUIREMENT_KINDS[r.kind]).map((r, i) => ({ id: r.id || `org-${r.kind}-${i}`, ...r, source: 'org' }));
  const auto = env !== 'sandbox' && ![...own, ...org].some((r) => r.kind === 'complete.no-placeholders')
    ? [{ id: `env-complete`, kind: 'complete.no-placeholders', text: `${env} runs have no placeholders`, params: {}, source: 'env' }]
    : [];
  return [...own, ...org, ...auto];
}

// ---- graph walking -----------------------------------------------------------------
function index(graph) {
  const blocks = graph?.blocks || [];
  const byId = Object.fromEntries(blocks.map((b) => [b.id, b]));
  const outs = Object.fromEntries(blocks.map((b) => [b.id, []]));
  for (const c of graph?.connections || []) if (outs[c.from]) outs[c.from].push(c.to);
  return { blocks, byId, outs };
}

// Every (source, target) pair joined by a path that crosses no `through`
// block. Same two-state DFS as agentOsProblems.
function ungated({ byId, outs }, sources, isTarget, isThrough) {
  const hits = [];
  for (const src of sources) {
    const seen = new Set();
    const stack = [[src.id, false]];
    while (stack.length) {
      const [id, gated] = stack.pop();
      const key = `${id}:${gated ? 1 : 0}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const node = byId[id];
      if (!node) continue;
      if (id !== src.id && isTarget(node) && !gated) hits.push({ from: src, to: node });
      const next = gated || (id !== src.id && isThrough(node));
      for (const to of outs[id] || []) stack.push([to, next]);
    }
  }
  return hits;
}

const label = (b) => b?.label || b?.type || b?.id || 'block';
const list = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
const isSideEffect = (b) => !!sideEffectKind(b?.type) && b.type !== 'control.preflight';
const isInput = (b) => /^input\./.test(b?.type || '');
const isSensitive = (b) => b?.classification === 'sensitive';
const masks = (b) => b?.type === 'logic.compute' && /\bmask\s*\(/.test(String(b.config?.assignments || ''));

// ---- static cost model (performance.budget) ----------------------------------------
const TIER_MS = { bulk: 3000, strong: 8000, frontier: 15000 };
const n = (v, d) => { const x = parseInt(v, 10); return Number.isFinite(x) && x > 0 ? x : d; };
function blockEstimate(b) {
  const c = b.config || {};
  const tier = TIER_MS[c.tier] || TIER_MS.strong;
  switch (b.type) {
    case 'transform.llm': return { ms: tier, calls: 1 };
    case 'control.decision': return { ms: TIER_MS[c.tier] || TIER_MS.bulk, calls: 1 };
    case 'transform.retrieve': return { ms: 6000, calls: 1 };
    case 'control.filter': return (c.mode || 'contains') === 'llm' ? { ms: TIER_MS.bulk, calls: 1 } : { ms: 50, calls: 0 };
    case 'loop.foreach': { const k = n(c.maxItems, 5); return { ms: k * TIER_MS.strong, calls: k }; }
    case 'loop.refine': { const k = n(c.maxRounds, 3); return { ms: k * 2 * TIER_MS.strong, calls: k * 2 }; }
    case 'loop.until': { const k = n(c.maxRounds, 3); const per = (c.conditionMode || 'contains') === 'llm' ? 2 : 1; return { ms: k * per * TIER_MS.strong, calls: k * per }; }
    case 'control.coordinate': return { ms: n(c.deadlineSec, 180) * 1000, calls: n(c.maxRounds, 2) * (n(c.maxAgents, 4) + 2) };
    case 'pipeline.call': return { ms: 20000, calls: 5 };
    case 'control.wait': return { ms: n(c.seconds, 5) * 1000, calls: 0 };
    case 'control.review': return { ms: 0, calls: 0, human: true };
    case 'container.run': return { ms: 60000, calls: 0 };
    case 'control.preflight': return { ms: 30000, calls: 0 };
    case 'input.http': return { ms: 2000, calls: 0 };
    case 'transform.graphql': return { ms: 1500, calls: 0 };
    default: return { ms: 200, calls: 0 };
  }
}

// Longest path in ms (DAG, memoized), total model calls (upper bound).
export function estimateGraph(graph) {
  const { blocks, byId, outs } = index(graph);
  const memo = {};
  const longest = (id, stack = new Set()) => {
    if (memo[id] !== undefined) return memo[id];
    if (stack.has(id)) return 0; // a cycle is topoSort's problem, not ours
    stack.add(id);
    const own = blockEstimate(byId[id]).ms;
    const below = (outs[id] || []).map((t) => longest(t, stack));
    stack.delete(id);
    memo[id] = own + (below.length ? Math.max(...below) : 0);
    return memo[id];
  };
  let wallMs = 0;
  for (const b of blocks) wallMs = Math.max(wallMs, longest(b.id));
  const modelCalls = blocks.reduce((a, b) => a + blockEstimate(b).calls, 0);
  const humanWaits = blocks.filter((b) => blockEstimate(b).human).length;
  return { wallMs, modelCalls, humanWaits };
}

// ---- the checkers --------------------------------------------------------------------
const CHECKS = {
  approval(graph, req, policy) {
    const idx = index(graph);
    const p = req.params || {};
    const kinds = list(p.targets);
    const from = list(p.fromBlocks);
    const sources = from.length ? idx.blocks.filter((b) => from.includes(b.id)) : idx.blocks.filter(isModelBlock);
    const isTarget = (b) => isSideEffect(b) && (!kinds.length || kinds.includes(sideEffectKind(b.type)));
    const hits = ungated(idx, sources, isTarget, (b) => b.type === 'control.review');
    if (!sources.length) return { status: 'met', detail: from.length ? 'the named blocks are not in the graph' : 'no model blocks — nothing to approve', blockIds: [] };
    if (!hits.length) return { status: 'met', detail: `${sources.length} source${sources.length === 1 ? '' : 's'}, every path to a write crosses a Human review`, blockIds: [] };
    return { status: 'unmet', detail: hits.slice(0, 3).map((h) => `${label(h.from)} reaches ${label(h.to)} with no Human review`).join('; '), blockIds: [...new Set(hits.flatMap((h) => [h.from.id, h.to.id]))] };
  },

  'integrity.validate-before-write'(graph) {
    const idx = index(graph);
    const sources = idx.blocks.filter(isInput);
    const writes = idx.blocks.filter(isSideEffect);
    if (!writes.length) return { status: 'met', detail: 'no side effects in the graph', blockIds: [] };
    const hits = ungated(idx, sources, isSideEffect, (b) => b.type === 'logic.validate' || b.type === 'control.preflight');
    if (!hits.length) return { status: 'met', detail: `${writes.length} write${writes.length === 1 ? '' : 's'}, every path from a read-in crosses a Validate`, blockIds: [] };
    return { status: 'unmet', detail: hits.slice(0, 3).map((h) => `${label(h.from)} reaches ${label(h.to)} without a Validate`).join('; '), blockIds: [...new Set(hits.flatMap((h) => [h.from.id, h.to.id]))] };
  },

  'integrity.sensitive-stays-local'(graph, req) {
    const idx = index(graph);
    const allowWebhook = String(req.params?.allowWebhook) === 'true';
    const sources = idx.blocks.filter(isSensitive);
    if (!sources.length) return { status: 'unmet', detail: 'no block is classified sensitive — mark the read-in that carries the data (block inspector → Classification)', blockIds: [] };
    const isTarget = (b) => isModelBlock(b) || (!allowWebhook && b.type === 'output.webhook');
    const hits = ungated(idx, sources, isTarget, masks);
    if (!hits.length) return { status: 'met', detail: `${sources.length} sensitive block${sources.length === 1 ? '' : 's'}; nothing reaches a model${allowWebhook ? '' : ' or webhook'} unmasked`, blockIds: [] };
    return { status: 'unmet', detail: hits.slice(0, 3).map((h) => `${label(h.from)} reaches ${label(h.to)} unmasked`).join('; '), blockIds: [...new Set(hits.flatMap((h) => [h.from.id, h.to.id]))] };
  },

  'performance.budget'(graph, req, policy, evidence) {
    const p = req.params || {};
    const wall = Number(p.wallMs) || 0;
    const calls = Number(p.modelCalls) || 0;
    if (!wall && !calls) return { status: 'unmet', detail: 'set an end-to-end target (ms) or a model-call cap', blockIds: [] };
    const est = estimateGraph(graph);
    const bits = [];
    if (wall) bits.push(`estimate ${Math.round(est.wallMs / 1000)}s of ${Math.round(wall / 1000)}s${est.humanWaits ? ` (+${est.humanWaits} human wait${est.humanWaits === 1 ? '' : 's'} excluded)` : ''}`);
    if (calls) bits.push(`≤${est.modelCalls} model calls of ${calls}`);
    if ((wall && est.wallMs > wall) || (calls && est.modelCalls > calls)) {
      return { status: 'unmet', detail: `over by estimate — ${bits.join(', ')}`, blockIds: [] };
    }
    const lastMs = Number(evidence?.lastRunMs);
    if (Number.isFinite(lastMs) && lastMs > 0 && wall) {
      return lastMs <= wall
        ? { status: 'met', detail: `${bits.join(', ')}; last run ${Math.round(lastMs / 1000)}s`, blockIds: [] }
        : { status: 'unmet', evidence: true, detail: `last run took ${Math.round(lastMs / 1000)}s, over the ${Math.round(wall / 1000)}s target (${bits.join(', ')})`, blockIds: [] };
    }
    return { status: wall ? 'needs-run' : 'met', detail: `${bits.join(', ')}${wall ? '; run once for evidence' : ''}`, blockIds: [] };
  },

  'complete.no-placeholders'(graph) {
    const stubs = (graph?.blocks || []).filter((b) => b.type === 'design.placeholder');
    if (!stubs.length) return { status: 'met', detail: 'every step is built', blockIds: [] };
    return { status: 'unmet', detail: `${stubs.length} placeholder${stubs.length === 1 ? '' : 's'}: ${stubs.map(label).join(', ')}`, blockIds: stubs.map((b) => b.id) };
  },
};

// ---- the verdict ---------------------------------------------------------------------
// `evidence`: { lastRunMs } from pipeline.lastRun when the caller has it.
// A structural `unmet` (not evidence-based) blocks a run outside sandbox;
// sandbox only warns so a design can be exercised while it is still open.
export function requirementProblems(graph, requirements, policy = null, evidence = {}) {
  const env = policy?.env || 'sandbox';
  const all = effectiveRequirements(requirements, policy);
  return all
    .map((req) => {
      const check = CHECKS[req.kind];
      const r = check ? check(graph, req, policy, evidence) : { status: 'unmet', detail: `unknown requirement kind ${req.kind}`, blockIds: [] };
      const blocking = r.status === 'unmet' && !r.evidence && env !== 'sandbox';
      return { id: req.id || `${req.kind}`, kind: req.kind, text: req.text || REQUIREMENT_KINDS[req.kind]?.label || req.kind, source: req.source || 'pipeline', storyId: req.storyId || null, params: req.params || {}, status: r.status, detail: r.detail, blockIds: r.blockIds || [], blocking };
    })
    .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));
}

export function requirementRefusals(results) {
  return (results || []).filter((r) => r.blocking).map((r) => `requirement "${r.text}" is not met: ${r.detail}`);
}

// The run-path fence beside assertAgentOsGraph (stream reconcile, host and
// block runners): throws the refusals, returns the results otherwise.
export function assertRequirements(graph, requirements, policy = null, evidence = {}) {
  const results = requirementProblems(graph, requirements, policy, evidence);
  const refusals = requirementRefusals(results);
  if (refusals.length) throw new Error(refusals.join('; '));
  return results;
}

export function requirementSummary(results) {
  const c = { met: 0, unmet: 0, 'needs-run': 0 };
  for (const r of results || []) c[r.status] = (c[r.status] || 0) + 1;
  return { ...c, total: (results || []).length, allMet: (results || []).length > 0 && c.unmet === 0 && c['needs-run'] === 0 };
}

// performance.budget narrows the run budget so the runtime enforces the
// target (shared/nqube/budget.js only ever narrows plane defaults).
export function requirementBudget(graph, requirements, policy) {
  const out = {};
  // Human review waits are outside the estimate; the runtime budget leaves
  // room for them (each gate's max wait) rather than failing a run a
  // reviewer was slow on.
  const reviewMs = (graph?.blocks || [])
    .filter((b) => b.type === 'control.review')
    .reduce((a, b) => a + Math.min(parseInt(b.config?.maxWaitMinutes, 10) || 30, 480) * 60 * 1000, 0);
  for (const r of effectiveRequirements(requirements, policy)) {
    if (r.kind !== 'performance.budget') continue;
    const wall = Number(r.params?.wallMs) || 0;
    const calls = Number(r.params?.modelCalls) || 0;
    if (wall) out.wallMs = Math.min(out.wallMs || Infinity, wall + reviewMs);
    if (calls) out.modelCalls = Math.min(out.modelCalls || Infinity, calls);
  }
  return out;
}
